import { caveAt } from '../../shared/game/caves.js';
import { naturalHeight, terrainHeight } from '../../shared/game/terrain.js';
import { samplePath } from '../../shared/game/path-curve.js';
import { pointSegmentDistance } from '../../shared/navigation/geometry.js';
import { PROP_BASE_SCALE, PROP_RADIUS } from '../../shared/game/prop-footprints.js';
import type { MapDefinition, MapPath, MapProp, Point, TerrainLevel } from '../../shared/game/world-types.js';
import { LAST_LIGHT } from './ember-coast-usable-props.js';
import { cartObstacle, fence, growThickets, growWoods, lampsAlong, solid, yard, type Layers } from './ember-coast-settlements.js';

/**
 * The Ember Coast's settlements and landmarks, placed on its reshaped land (`ember-coast-land.ts`) along its roads
 * (`ember-coast-ways.ts`): on level ground clear of cliffs and roads, doors toward the camera. Each helper keeps collision
 * (`obstacles`) aligned with what is drawn (`props`).
 */
export function addPlaces(layers: Layers) {
  const { props, obstacles, paths } = layers;
  const house = (x: number, z: number, variant: number, rotation = 0, lit = false) => {
    props.push({ kind: 'house', x, z, variant, rotation, lit });
    const across = Math.abs(Math.sin(rotation)) > .7, size = PROP_BASE_SCALE.house!;
    solid(layers, x, z, (across ? 8 : 10) * size, (across ? 10 : 8) * size, 'building');
  };
  // Sizes are as authored before the kind's base scale shrank the drawing; the obstacle shrinks with it.
  const piece = (prop: MapProp, width: number, depth = width) => { const size = PROP_BASE_SCALE[prop.kind] ?? 1; props.push(prop); solid(layers, prop.x, prop.z, width * size, depth * size); };
  const cart = (prop: MapProp) => { props.push(prop); obstacles.push(cartObstacle(prop)); };

  // Pilgrim's Crossing: the wayfarers' camp, cart, banner and shrine where the two roads cross, laid out as they always were,
  // among the broken walls of the old settlement that stood here before the roads.
  props.push({ kind: 'camp', x: 26, z: -15, variant: 1 }, { kind: 'sign', x: -4, z: 11, rotation: -.4 }, { kind: 'banner', x: 4, z: -14, variant: 1, rotation: -.15 },
    { kind: 'lamp-post', x: -6, z: -5, rotation: -1 });
  cart({ kind: 'cart', x: 12, z: 15, rotation: .2 });
  piece({ kind: 'woodpile', x: 32, z: -21, rotation: .4 }, 3, 2);
  piece({ kind: 'shrine', x: -13, z: -17, rotation: .6 }, 2.4);
  obstacles.push({ x: -8, z: -14, width: 5, depth: 2, kind: 'ruin' }, { x: -9.5, z: -11, width: 2, depth: 4, kind: 'ruin' }, { x: 24, z: 19, width: 2, depth: 4, kind: 'ruin' });
  // The old settlement: a temple's shell north-west of the crossing, house footings in every quarter, its well, its
  // graves and a fallen watchtower, all standing back from the roads.
  foundation(layers, -24, -24, 14, 10, ['s1', 'e2']);
  foundation(layers, -46, -24, 8, 7, ['n2', 'w1', 's2']);
  foundation(layers, 30, -44, 9, 7, ['s2', 'w2']);
  foundation(layers, 34, 42, 9, 7, ['n1', 'e1']);
  foundation(layers, -24, 28, 9, 7, ['e2', 'n1', 's2']);
  foundation(layers, -12, 48, 7, 6, ['w1', 'n2', 'e1']);
  foundation(layers, -8, -44, 7, 6, ['n1', 'e2']);
  foundation(layers, -52, -4, 8, 6, ['s2', 'e1', 'w2']);
  foundation(layers, -40, -48, 10, 8, ['n2', 'w1', 'e2']);
  foundation(layers, 44, -8, 8, 6, ['w1', 's2']);
  foundation(layers, 56, -32, 9, 7, ['n1', 'e1', 's2']);
  foundation(layers, 58, 40, 8, 6, ['n2', 'w2']);
  foundation(layers, -42, 28, 7, 6, ['e1', 's1']);
  foundation(layers, -6, 68, 8, 6, ['n2', 'e2', 'w1']);
  obstacles.push({ x: 30, z: -30, width: 6, depth: .9, kind: 'ruin' }, { x: 47, z: -22, width: .9, depth: 6, kind: 'ruin' },
    { x: -34, z: -8, width: 4, depth: .9, kind: 'ruin' }, { x: 50, z: 50, width: 5, depth: .9, kind: 'ruin' },
    { x: -14, z: -32, width: 4, depth: .9, kind: 'ruin' }, { x: 6, z: -58, width: .9, depth: 4, kind: 'ruin' }, { x: -56, z: 32, width: .9, depth: 5, kind: 'ruin' }, { x: 66, z: 8, width: 5, depth: .9, kind: 'ruin' });
  piece({ kind: 'obelisk', x: -24, z: -38, rotation: .3 }, 1.6);
  piece({ kind: 'well', x: 20, z: 52 }, 3);
  for (const [x, z, rotation] of [[46, 62, .1], [49.5, 63, -.1], [53, 61.5, .2], [47.5, 66.5, 0]]) props.push({ kind: 'grave', x, z, rotation });
  props.push({ kind: 'cairn', x: -38, z: 40, rotation: 1 }, { kind: 'cairn', x: 42, z: -58, rotation: 2 });
  props.push({ kind: 'ruined-tower', x: -34, z: 62, rotation: .8 }); solid(layers, -34, 62, 8, 8, 'building');

  // Greywake: the deserted village on its heights, the coast road running past its market square and well.
  for (const [i, [x, z]] of [[-214, -100], [-232, -88], [-150, -100], [-128, -86], [-246, -108], [-140, -48], [-112, -66], [-150, -26], [-126, -18], [-206, -118]].entries())
    house(x, z, i % 4);
  piece({ kind: 'well', x: -178, z: -84 }, 3);
  for (const [x, z, rotation, variant] of [[-192, -90, .1, 0], [-178, -100, -.1, 1], [-190, -76, 0, 2]] as const) piece({ kind: 'stall', x, z, rotation, variant }, 4.4, 3.6);
  piece({ kind: 'crates', x: -196, z: -100 }, 3.4, 3); piece({ kind: 'crates', x: -190, z: -112, rotation: .6 }, 3.4, 3); piece({ kind: 'haystack', x: -128, z: -32 }, 4);
  props.push({ kind: 'laundry', x: -222, z: -114, rotation: .05 }, { kind: 'laundry', x: -134, z: -98, rotation: Math.PI / 2 + .1 });
  for (const [x, z] of [[-258, -94], [-122, -102], [-150, -118], [-100, -50]]) piece({ kind: 'dead-tree', x, z, rotation: x }, 1.4);
  props.push({ kind: 'scarecrow', x: -252, z: -100, rotation: .6 }, { kind: 'scarecrow', x: -116, z: -110, rotation: -.4 });
  yard(layers, -110, -96, 3, 3);
  lampsAlong(layers, [{ x: -60, z: -12 }, { x: -110, z: -44 }, { x: -150, z: -72 }, { x: -168, z: -112 }, { x: -200, z: -154 }], 6, 24, 1, { x: -160, z: -70, radius: 60 });

  // Wrack Cove: a fishing hamlet on the strand under the moor, a jetty out into the bay. One lamp is still lit.
  house(-276, -148, 0, .2); house(-300, -144, 3, -.2, true); house(-324, -146, 2, Math.PI / 2);
  props.push({ kind: 'jetty', x: -364, z: -162, rotation: Math.PI / 2 }, { kind: 'rowboat', x: -340, z: -150, rotation: .3 }, { kind: 'rowboat', x: -336, z: -170, rotation: -.5 },
    { kind: 'lobster-pots', x: -338, z: -148 }, { kind: 'anchor', x: -312, z: -130, rotation: .8 });
  piece({ kind: 'net-rack', x: -300, z: -176 }, 7, 1); piece({ kind: 'net-rack', x: -322, z: -174, rotation: .5 }, 7, 1); piece({ kind: 'crates', x: -286, z: -132 }, 3.4, 3);
  paths.push({ kind: 'trail', width: 3, points: [{ x: -250, z: -174 }, { x: -280, z: -166 }, { x: -312, z: -162 }, { x: -344, z: -160 }] });

  // Last Light: the lighthouse on the headland's crest, the keeper's house behind it, a signal brazier on the brow.
  piece({ kind: 'lighthouse', x: LAST_LIGHT.x, z: LAST_LIGHT.z, id: LAST_LIGHT.id }, 10);
  house(-210, 198, 2, Math.PI / 2, true);
  piece({ kind: 'brazier', x: -240, z: 182 }, 1.6); piece({ kind: 'haystack', x: -196, z: 214 }, 4);
  props.push({ kind: 'laundry', x: -204, z: 212, rotation: .1 });
  lampsAlong(layers, [{ x: -118, z: 138 }, { x: -170, z: 176 }, { x: -232, z: 190 }], 5, 26, -1, { x: -190, z: 180, radius: 50 });

  // The Drowned Hull in the shallows off the tidal flats, whale bones and tide pools along the strand.
  props.push({ kind: 'wreck', x: -282, z: 70, rotation: -.65, scale: 1.5 }, { kind: 'boat', x: -268, z: 112, rotation: .4 },
    { kind: 'whale-bones', x: -226, z: 96, rotation: 1.1, scale: 1.1 }, { kind: 'tide-pool', x: -250, z: 60 }, { kind: 'tide-pool', x: -238, z: 104, scale: .8 }, { kind: 'bones', x: -230, z: 74 });

  // Greymere: a boardwalk across the marsh on the gully trail, an abandoned reed-cutter's hut and camp beside it.
  props.push({ kind: 'bridge', x: -158, z: 116, variant: 24, rotation: Math.atan2(68, 50) }, { kind: 'camp', x: -132, z: 90 });
  house(-150, 82, 1, .4);
  for (const [x, z] of [[-174, 74], [-196, 104], [-124, 70]]) piece({ kind: 'dead-tree', x, z, rotation: x }, 1.4);

  // Saint's Rest: the chapel and its graveyard beside the north road in Northwood.
  props.push({ kind: 'chapel', x: -116, z: -336, rotation: .08 }); solid(layers, -116, -336, 13 * PROP_BASE_SCALE.chapel!, 20 * PROP_BASE_SCALE.chapel!, 'building');
  for (let i = 0; i < 7; i++) props.push({ kind: 'grave', x: -142 + i % 3 * 5, z: -326 + Math.floor(i / 3) * 6, variant: i });
  piece({ kind: 'shrine', x: -100, z: -322, rotation: -.3 }, 2.4);

  // Saltgrass Mill: the windmill on a knoll above the south road, the farmhouse, fields, haystacks and scarecrows.
  piece({ kind: 'windmill', x: 140, z: 300, lit: true }, 8.4);
  house(162, 354, 2, Math.PI, true);
  cart({ kind: 'cart', x: 134, z: 334, rotation: .8 });
  piece({ kind: 'crates', x: 146, z: 356 }, 3.4, 3); props.push({ kind: 'lamp-post', x: 152, z: 364, rotation: 3 });
  for (const [x, z] of [[186, 322], [200, 340], [176, 400], [168, 290]]) piece({ kind: 'haystack', x, z }, 4);
  props.push({ kind: 'scarecrow', x: 196, z: 366, rotation: .2 }, { kind: 'scarecrow', x: 160, z: 390, rotation: -.7 }, { kind: 'scarecrow', x: 232, z: 350, rotation: 1 });
  yard(layers, 166, 310, 4, 3); yard(layers, 180, 378, 5, 3);
  paths.push({ kind: 'trail', width: 3, points: [{ x: 96.9, z: 311.7 }, { x: 120, z: 318 }, { x: 140, z: 330 }, { x: 150, z: 340 }] });

  // East Watch: the stone watchtower at the head of its cleft, braziers, a banner and palisade runs on the ridge top.
  piece({ kind: 'watchtower', x: 576, z: 16, rotation: -.1 }, 7.2);
  piece({ kind: 'brazier', x: 552, z: 22 }, 1.6); piece({ kind: 'brazier', x: 586, z: 40 }, 1.6); piece({ kind: 'crates', x: 590, z: 4 }, 3.4, 3);
  props.push({ kind: 'banner', x: 560, z: 6, variant: 0 });
  fence(layers, 540, -10, 6, 'east'); fence(layers, 540, -10, 4, 'south');

  // Copper Pine: the hunters' lodge in the woods on the ridge's eastern top.
  house(586, -40, 2, -.6, true);
  piece({ kind: 'woodpile', x: 572, z: -30, rotation: .3 }, 3, 2); piece({ kind: 'crates', x: 600, z: -54, rotation: .4 }, 3.4, 3);
  props.push({ kind: 'lamp-post', x: 574, z: -50, rotation: 2 }, { kind: 'cairn', x: 540, z: -60 });

  // Beacon Point: a signal fire and cairns on the moor's sea cliffs north of Gull Fjord.
  piece({ kind: 'brazier', x: -436, z: -398, scale: 1.4 }, 2); piece({ kind: 'cairn', x: -426, z: -408 }, 2.4); piece({ kind: 'cairn', x: -448, z: -384, scale: .8 }, 2);
  props.push({ kind: 'banner', x: -430, z: -388, variant: 0 });

  // The Windward Stones: a ring and an obelisk below the Windward Edge, cairns marking the way.
  props.push({ kind: 'standing-stones', x: 392, z: 330, scale: 1.3 });
  piece({ kind: 'obelisk', x: 392, z: 330 }, 3.6); piece({ kind: 'cairn', x: 340, z: 338 }, 2.4); piece({ kind: 'cairn', x: 430, z: 318, scale: 1.2 }, 2.8);
  piece({ kind: 'brazier', x: 376, z: 352 }, 1.6);

  // The Hollow: the bandits hold the caves. Torches and a barricade at the gatehouse, their camp in the great hall, stores in the
  // storeroom and armoury, the pens fenced, the chief in his sunken den, the smugglers' hoard in the vault, the dead in the
  // sunken crypt, an old shrine and a grotto of stalagmites deep in.
  props.push({ kind: 'torch-wall', x: 298, z: -58 }, { kind: 'torch-wall', x: 316, z: -58, rotation: Math.PI }, { kind: 'banner', x: 306, z: -70, variant: 1 });
  fence(layers, 306, -88, 1, 'east'); fence(layers, 313, -88, 1, 'east');
  for (const [x, z] of [[316, -132], [334, -150], [300, -150]]) props.push({ kind: 'camp', x, z });
  piece({ kind: 'brazier', x: 320, z: -144 }, 1.6); piece({ kind: 'crates', x: 334, z: -132, rotation: .3 }, 3.4, 3); props.push({ kind: 'banner', x: 318, z: -122, variant: 1 });
  for (const [x, z] of [[414, -80], [426, -70], [366, -222]]) piece({ kind: 'crates', x, z, rotation: x * .01 }, 3.4, 3);
  yard(layers, 226, -214, 4, 3);
  props.push({ kind: 'camp', x: 422, z: -298 }, { kind: 'chest', x: 430, z: -306 }, { kind: 'banner', x: 412, z: -304, variant: 1 });
  props.push({ kind: 'chest', x: 516, z: -226 }, { kind: 'chest', x: 526, z: -232 }, { kind: 'satchel', x: 510, z: -220 });
  piece({ kind: 'crates', x: 518, z: -216 }, 3.4, 3);
  for (let i = 0; i < 6; i++) props.push({ kind: 'grave', x: 244 + i % 3 * 4, z: -292 + Math.floor(i / 3) * 6, variant: i + 1 });
  props.push({ kind: 'bones', x: 244, z: -272, rotation: 1.4 }, { kind: 'shrine', x: 150, z: -248, rotation: .3 }, { kind: 'bones', x: 520, z: -116, rotation: .5 });
  for (const [x, z] of [[140, -345], [146, -356], [232, -410], [446, -180]]) props.push({ kind: 'stalagmites', x, z });
  // Torches on the walls of the busiest chambers: gatehouse, great hall, armoury, den and vault.
  for (const [x, z, radius] of [[306, -63, 20], [320, -141, 30], [370, -225, 15], [420, -295, 22], [520, -225, 18]])
    for (const angle of [.6, 2.4, 4.2]) props.push({ kind: 'torch-wall', x: Math.round(x + Math.cos(angle) * radius * .6), z: Math.round(z + Math.sin(angle) * radius * .6), rotation: angle + Math.PI });
  // A stone arch spans the Hollow's main gorge where it opens in the ridge's face, its piers against the walls, square to the
  // gorge so the trail runs through it.
  props.push({ kind: 'cave-mouth', x: 303, z: -19, rotation: -Math.atan2(3, 26), scale: .55 });

  // Wayside: milestones along the roads, shrines at the forks, two ruined towers, hives by the mill, buoys off the coast.
  milestones(layers, paths.filter(path => path.kind === 'road'), 115);
  for (const [i, [x, z, rotation]] of [[-140, -84, 2.6], [-4, 84, -2.4], [246, 80, 3], [60, 190, -1.8], [-236, -182, 1.2], [524, 200, 2.2]].entries())
    props.push({ kind: 'shrine', x, z, rotation, variant: i % 3 });
  for (const [x, z, rotation] of [[204, 176, .4], [-104, -184, 2.1]]) { props.push({ kind: 'ruined-tower', x, z, rotation }); solid(layers, x, z, 8, 8, 'building'); }
  piece({ kind: 'beehives', x: 214, z: 334, rotation: .3 }, 3.4, 1.2);
  for (const [x, z] of [[-380, -150], [-292, 92], [-306, 180], [-560, -300], [-420, -40], [-132, 322]]) props.push({ kind: 'buoy', x, z });

  // Small furniture belongs beside the roads, never on them.
  for (let pass = 0; pass < 4; pass++) { nudgeOffRoads(layers, ['sign', 'banner', 'lamp-post', 'milestone'], .6); nudgeOffRoads(layers, ['shrine'], 2.2); }
}

/** A fallen building's footing: its four walls, each in two lengths with a doorway between, leaving out the named lengths ('n1' is the north wall's west half). */
function foundation({ obstacles }: Layers, x: number, z: number, width: number, depth: number, fallen: string[]) {
  // Gaps are doorways a hero walks through. Narrower ones look open yet only just let a hero by; 3.6 m lets two pass.
  const thick = .9, gap = 3.6;
  for (const [side, along, fixed] of [['n', 'x', z - depth / 2], ['s', 'x', z + depth / 2], ['w', 'z', x - width / 2], ['e', 'z', x + width / 2]] as const) {
    const span = along === 'x' ? width : depth, centre = along === 'x' ? x : z, half = (span - gap) / 2;
    for (const part of [1, 2]) {
      if (fallen.includes(side + part)) continue;
      const middle = centre + (part === 1 ? -1 : 1) * (gap / 2 + half / 2);
      obstacles.push(along === 'x' ? { x: middle, z: fixed, width: half, depth: thick, kind: 'ruin' } : { x: fixed, z: middle, width: thick, depth: half, kind: 'ruin' });
    }
  }
}

/** Pushes each small piece of the given kinds out to just past the nearest way's drawn edge (its width plus 1.25 m). */
function nudgeOffRoads({ props, paths }: Layers, kinds: MapProp['kind'][], clearance: number) {
  const curves = paths.map(path => ({ path, points: samplePath(path.points, 600) }));
  for (const prop of props.filter(p => kinds.includes(p.kind))) {
    let best: { q: Point; d: number; path: MapPath } | null = null;
    for (const { path, points } of curves) for (const q of points) {
      const d = Math.hypot(prop.x - q.x, prop.z - q.z);
      if (!best || d - path.width / 2 < best.d - best.path.width / 2) best = { q, d, path };
    }
    if (!best) continue;
    const reach = best.path.width / 2 + 1.25 + clearance;
    if (best.d >= reach) continue;
    const dx = best.d > .01 ? (prop.x - best.q.x) / best.d : 1, dz = best.d > .01 ? (prop.z - best.q.z) / best.d : 0;
    prop.x = Math.round((best.q.x + dx * reach) * 10) / 10; prop.z = Math.round((best.q.z + dz * reach) * 10) / 10;
  }
}

/** Milestones every `spacing` metres along each road, standing just past its edge on alternating sides. */
function milestones({ props }: Layers, roads: MapPath[], spacing: number) {
  for (const [index, road] of roads.entries()) {
    const curve = samplePath(road.points, 600);
    let travelled = 0, next = spacing * .6, side = index % 2 ? 1 : -1;
    for (let i = 1; i < curve.length; i++) {
      const a = curve[i - 1], b = curve[i], length = Math.hypot(b.x - a.x, b.z - a.z);
      travelled += length;
      if (travelled < next) continue;
      next += spacing; side = -side;
      if (Math.abs(b.x) > 596 || Math.abs(b.z) > 436) continue;
      const nx = -(b.z - a.z) / length * side, nz = (b.x - a.x) / length * side, offset = road.width / 2 + 2.1;
      props.push({ kind: 'milestone', x: Math.round((b.x + nx * offset) * 10) / 10, z: Math.round((b.z + nz * offset) * 10) / 10, rotation: Math.atan2(nx, nz) });
    }
  }
}

/**
 * Level pads under buildings, camps and carts, so each stands flat on its ground: the natural ground beneath is held at its
 * height at the prop, and any plateau it stands on adds the same over the whole pad.
 */
export function foundations(map: MapDefinition, props: MapProp[]): TerrainLevel[] {
  const kinds = new Set<MapProp['kind']>(['house', 'chapel', 'windmill', 'watchtower', 'lighthouse', 'camp', 'standing-stones', 'well', 'cart', 'stall']);
  return props.filter(prop => kinds.has(prop.kind)).map(prop => {
    const radius = PROP_RADIUS[prop.kind] * (prop.scale ?? 1) + 2;
    return { x: prop.x, z: prop.z, width: radius * 2, depth: radius * 2, elevation: naturalHeight(map, prop.x, prop.z), blend: Math.max(6, radius) };
  });
}

/** The places' names, shown over the map. */
export const placeNames = [
  { name: 'PILGRIM’S CROSSING', x: 0, z: 12 },
  { name: 'GREYWAKE · DESERTED VILLAGE', x: -170, z: -124 },
  { name: 'WRACK COVE · FISHING HAMLET', x: -300, z: -128 },
  { name: 'LAST LIGHT · LIGHTHOUSE', x: -240, z: 176 },
  { name: 'THE DROWNED HULL', x: -276, z: 50 },
  { name: 'GREYMERE MARSH', x: -160, z: 96 },
  { name: 'SAINT’S REST', x: -120, z: -360 },
  { name: 'SALTGRASS MILL', x: 140, z: 318 },
  { name: 'EAST WATCH', x: 576, z: 0 },
  { name: 'COPPER PINE LODGE', x: 586, z: -62 },
  { name: 'BEACON POINT', x: -436, z: -416 },
  { name: 'THE WINDWARD STONES', x: 392, z: 352 },
  { name: 'THE HOLLOW · BANDITS’ CAVES', x: 300, z: 30 },
  { name: 'SOUTHSTRAND', x: -10, z: 360 },
];

/**
 * Woods and thickets on the finished ground: trunks in Northwood, on Copper Pine's ridge top and in a few copses, then bushes
 * and boulders by biome. Nothing grows on a road or within a few metres of one, on ground at or below the sea, on a slope
 * too steep to walk, or on a cave's roof. Seeded, so server and browser grow the same cover. Call once the map's ground is final.
 */
export function growCover(map: MapDefinition, layers: Layers) {
  let seed = 90210;
  const random = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
  const curves = layers.paths.map(path => ({ path, points: samplePath(path.points, path.points.length * 24) }));
  const keepClear = (p: Point) => {
    if (Math.abs(p.x) > map.width / 2 - 4 || Math.abs(p.z) > map.depth / 2 - 4) return false;
    // Nothing grows on a cave's roof, which falls away when the party goes in below.
    if (caveAt(map, p.x, p.z)) return false;
    const height = terrainHeight(map, p.x, p.z);
    if (height < .5) return false;
    const grade = Math.hypot(terrainHeight(map, p.x + 1.5, p.z) - terrainHeight(map, p.x - 1.5, p.z), terrainHeight(map, p.x, p.z + 1.5) - terrainHeight(map, p.x, p.z - 1.5)) / 3;
    if (grade > .45) return false;
    return !curves.some(({ path, points }) => points.some((b, i) => i > 0 && Math.abs(b.x - p.x) < 30 && Math.abs(b.z - p.z) < 30 && pointSegmentDistance(p, points[i - 1], b) < path.width / 2 + 5));
  };
  growWoods(layers, random, [
    { x: -60, z: -270, radius: 140, count: 45 }, { x: 520, z: 10, radius: 100, count: 30 }, { x: 120, z: -20, radius: 60, count: 12 },
    { x: 470, z: 240, radius: 70, count: 12 }, { x: -262, z: -350, radius: 50, count: 10 },
  ], keepClear);
  growThickets(layers, random, map.scenery!.districts, keepClear);
}
